import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import {
   ArrowLeft,
   CreditCard,
   Landmark,
   Mail,
   MapPin,
   Phone,
   ShieldCheck,
   ShoppingBag,
   Trash2,
   User,
} from "lucide-react";
import { supabase } from "../../supabase-client";
import TransferInstructions from "../../components/TransferInstructions";
import {
   processPayment,
   validateMercadoPagoData,
} from "../../services/mercadoPagoService";
import getDolar from "../../utils/cotDolar";
import Enviarmail from "../../components/enviarmail";
import useAdministradores from "../../components/getadministradores";

export default function Carrito() {
   const token = process.env.REACT_APP_SUPABASE_ANON_KEY;
   const navigate = useNavigate();
   const cookies = new Cookies();
   const divTransfRef = useRef(null);

   const { administradores } = useAdministradores();

   const [dolarRate, setDolarRate] = useState(0);
   const [paymentMethod, setPaymentMethod] = useState("transferencia");
   const [tabla, setTabla] = useState([]);
   const [userRecord, setUserRecord] = useState({
      email: "",
      nombre: "",
      documento: 1,
      celular: "",
      perfil: 18,
   });
   const [dirRecord, setDirRecord] = useState({
      calle: "",
      ciudad: "",
      provincia: "",
      codigo_postal: "",
   });
   const [cartObras, setCartObras] = useState([]);
   const [userFound, setUserFound] = useState(false);
   const [isProcessing, setIsProcessing] = useState(false);
   const [currentUserId, setCurrentUserId] = useState(null);
   const [textoPago, setTextoPago] = useState("");

   useEffect(() => {
      const boot = async () => {
         const rate = await getDolar();
         setDolarRate(rate);
         await Promise.all([fetchTabla(), fetchUser(), fetchCartObras()]);
      };

      boot();
   }, []);

   useEffect(() => {
      if (paymentMethod === "transferencia") {
         setTextoPago("Confirmar compra");
         setTimeout(() => {
            if (divTransfRef.current) {
               divTransfRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
               });
            }
         }, 120);
      } else {
         setTextoPago("Pagar con Mercado Pago");
      }
   }, [paymentMethod]);

   const fetchTabla = async () => {
      const { error, data } = await supabase
         .from("tabla")
         .select("*")
         .eq("cod", 6)
         .gt("orden", 1)
         .order("cod", { ascending: true })
         .order("orden", { ascending: true });

      if (error) {
         console.error("Error reading tabla:", error.message);
         return;
      }

      setTabla(data || []);
   };

   const fetchUser = async () => {
      const { data: user, error: fetchError } = await supabase
         .from("usuarios")
         .select("*")
         .eq("password_hash", localStorage.getItem("authjwt"))
         .single();

      if (fetchError) {
         console.error("Error fetching user:", fetchError);
         return;
      }

      if (!user) {
         console.error("No user found with the provided token");
         return;
      }

      setUserRecord((prev) => ({
         ...prev,
         email: user.email || "",
         nombre: user.nombre || "",
         celular: user.celular || "",
      }));
      setCurrentUserId(user.id);
      setUserFound(true);

      const { data: dirData, error: dirError } = await supabase
         .from("direcciones")
         .select("calle, ciudad, provincia, codigo_postal")
         .eq("usuario_id", user.id)
         .single();

      if (!dirError && dirData) {
         setDirRecord({
            calle: dirData.calle || "",
            ciudad: dirData.ciudad || "",
            provincia: dirData.provincia || "",
            codigo_postal: dirData.codigo_postal || "",
         });
      }
   };

   const fetchCartObras = async () => {
      let cart = cookies.get("obra") || [];
      if (!Array.isArray(cart)) cart = [];

      if (cart.length === 0) {
         setCartObras([]);
         return;
      }

      const { data, error } = await supabase
         .from("obras")
         .select("id, titulo, medidas, precio, imagenes(imagen_url)")
         .in("id", cart.map(Number));

      if (error) {
         console.error("Error fetching cart obras:", error.message);
         return;
      }

      setCartObras(data || []);
   };

   const totalUSD = useMemo(() => {
      return cartObras.reduce(
         (total, obra) => total + Number(obra.precio || 0),
         0,
      );
   }, [cartObras]);

   const totalPagoPesos = useMemo(() => {
      return totalUSD * dolarRate;
   }, [totalUSD, dolarRate]);

   const totalPagoPesosFormatted = useMemo(() => {
      return totalPagoPesos.toLocaleString("es-AR", {
         maximumFractionDigits: 0,
      });
   }, [totalPagoPesos]);

   const isAddressComplete = () => {
      return (
         dirRecord.calle.trim() !== "" &&
         dirRecord.ciudad.trim() !== "" &&
         dirRecord.provincia.trim() !== "" &&
         dirRecord.codigo_postal.trim() !== ""
      );
   };

   const isUserDataComplete = () => {
      return userRecord.nombre.trim() !== "" && userRecord.email.trim() !== "";
   };

   const isFormComplete = () => {
      return (
         isAddressComplete() &&
         isUserDataComplete() &&
         cartObras.length > 0 &&
         paymentMethod !== ""
      );
   };

   const handleChange = (e) => {
      const { name, value } = e.target;
      setUserRecord((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   const handleDirChange = (e) => {
      const { name, value } = e.target;
      setDirRecord((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   const validateForm = () => {
      const errors = [];

      if (!userRecord.nombre.trim()) errors.push("Nombre es requerido");
      if (!userRecord.email.trim()) errors.push("Email es requerido");
      if (!dirRecord.calle.trim()) errors.push("Calle es requerida");
      if (!dirRecord.ciudad.trim()) errors.push("Ciudad es requerida");
      if (!dirRecord.provincia.trim()) errors.push("Provincia es requerida");
      if (!dirRecord.codigo_postal.trim()) {
         errors.push("Código postal es requerido");
      }
      if (cartObras.length === 0) errors.push("El carrito está vacío");

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (userRecord.email && !emailRegex.test(userRecord.email)) {
         errors.push("Email no válido");
      }

      return errors;
   };

   const clearCart = () => {
      cookies.remove("obra", { path: "/" });
      setCartObras([]);
      window.dispatchEvent(new Event("cartUpdated"));
   };

   const handleDelete = async (id) => {
      let cart = cookies.get("obra") || [];
      if (!Array.isArray(cart)) cart = [];

      cart = cart.filter((obraId) => Number(obraId) !== Number(id));
      cookies.set("obra", cart, { path: "/" });
      setCartObras((prev) => prev.filter((obra) => obra.id !== id));
      window.dispatchEvent(new Event("cartUpdated"));

      const { error } = await supabase
         .from("obras")
         .update({ estado: 2 })
         .eq("id", id);

      if (cart.length === 0) {
         navigate("/");
      }

      if (error) {
         console.error("Error restoring obra state:", error.message);
      }
   };

   const handleCancelacion = async () => {
      try {
         for (const obra of cartObras) {
            const { error } = await supabase
               .from("obras")
               .update({ estado: 2 })
               .eq("id", obra.id);

            if (error) {
               console.error(`Error updating obra ${obra.id}:`, error.message);
            }
         }

         clearCart();
         navigate("/");
      } catch (error) {
         console.error("Error during cancellation:", error);
         alert("Error al cancelar la compra");
      }
   };

   const handleComprar = async () => {
      setIsProcessing(true);

      try {
         const validationErrors = validateForm();
         if (validationErrors.length > 0) {
            alert("Errores de validación:\n" + validationErrors.join("\n"));
            setIsProcessing(false);
            return;
         }

         if (!currentUserId) {
            alert("No se pudo identificar el usuario actual");
            setIsProcessing(false);
            return;
         }

         const userId = currentUserId;

         const { error: updateUserError } = await supabase
            .from("usuarios")
            .update({
               nombre: userRecord.nombre,
               email: userRecord.email,
               celular: userRecord.celular,
               perfil: userRecord.perfil,
            })
            .eq("id", currentUserId);

         if (updateUserError) {
            throw new Error(`Error updating user: ${updateUserError.message}`);
         }

         const { data: existingAddress, error: addressCheckError } = await supabase
            .from("direcciones")
            .select("id")
            .eq("usuario_id", userId)
            .single();

         if (existingAddress && !addressCheckError) {
            const { error: updateAddressError } = await supabase
               .from("direcciones")
               .update({
                  calle: dirRecord.calle,
                  ciudad: dirRecord.ciudad,
                  provincia: dirRecord.provincia,
                  codigo_postal: dirRecord.codigo_postal,
               })
               .eq("usuario_id", userId);

            if (updateAddressError) {
               throw new Error(`Error updating address: ${updateAddressError.message}`);
            }
         } else {
            const { error: insertAddressError } = await supabase
               .from("direcciones")
               .insert([
                  {
                     usuario_id: userId,
                     calle: dirRecord.calle,
                     ciudad: dirRecord.ciudad,
                     provincia: dirRecord.provincia,
                     codigo_postal: dirRecord.codigo_postal,
                     created_at: new Date().toISOString(),
                  },
               ]);

            if (insertAddressError) {
               throw new Error(`Error creating address: ${insertAddressError.message}`);
            }
         }

         const totalAmount = cartObras.reduce(
            (total, obra) => total + Number(obra.precio || 0),
            0,
         );

         const cartObrasEnhanced = cartObras.map((obra) => ({
            id: obra.id,
            titulo: obra.titulo,
            precio_usd: Number(obra.precio || 0),
            precio_pesos: Number(obra.precio || 0) * dolarRate,
            medidas: obra.medidas,
            imagenes: obra.imagenes,
         }));

         const { data: newOrder, error: orderError } = await supabase
            .from("pedidos")
            .insert([
               {
                  usuario_id: userId,
                  obras: JSON.stringify(cartObrasEnhanced),
                  total: totalAmount,
                  total_pesos: totalAmount * dolarRate,
                  dolar_rate: dolarRate,
                  estado: "pendiente",
                  fecha_pedido: new Date().toISOString(),
                  direccion_entrega: JSON.stringify(dirRecord),
               },
            ])
            .select()
            .single();

         if (orderError) {
            throw new Error(`Error creating order: ${orderError.message}`);
         }

         if (paymentMethod !== "transferencia") {
            const cartObrasForMercadoPago = cartObrasEnhanced.map((obra) => ({
               id: obra.id,
               titulo: obra.titulo,
               precio: obra.precio_pesos,
               medidas: obra.medidas,
               currency_id: "ARS",
            }));

            const orderData = {
               newOrder,
               cartObras: cartObrasForMercadoPago,
               userRecord,
               dirRecord,
               totalAmount: totalAmount * dolarRate,
            };

            const validation = validateMercadoPagoData(orderData);
            if (!validation.isValid) {
               alert(`Validation errors:\n${validation.errors.join("\n")}`);
               setIsProcessing(false);
               return;
            }

            const paymentResult = await processPayment(orderData, token, true);

            if (paymentResult.success) {
               clearCart();
            } else {
               throw new Error(paymentResult.message || "No se pudo iniciar el pago");
            }
         } else {
            let datomail = `
            <h1>Nueva Compra realizada</h1>
            <p>Se ha realizado un nuevo pedido por parte de ${userRecord.nombre}.</p>
            <p><strong>Detalles del pedido:</strong></p>
            <ul>
               <li>ID de usuario: ${userId}</li>
               <li>Nombre: ${userRecord.nombre}</li>
               <li>Telefono: ${userRecord.celular}</li>
               <li>Total USD: U$S ${totalAmount.toLocaleString()}</li>
               <li>Total Pesos: $ ${(totalAmount * dolarRate).toLocaleString()}</li>
               <li>Cotización Dólar: $ ${dolarRate.toLocaleString()}</li>
               <li>Fecha: ${new Date().toLocaleString()}</li>
            </ul>
            <p>Domicilio de entrega:</p>
            <ul>
               <li>Calle: ${dirRecord.calle}</li>
               <li>Ciudad: ${dirRecord.ciudad}</li>
               <li>Provincia: ${dirRecord.provincia}</li>
               <li>Código Postal: ${dirRecord.codigo_postal}</li>
            </ul>
            <p>Obras compradas:</p>
            <ul>
            `;

            cartObrasEnhanced.forEach((obra) => {
               datomail += `
               <li>
                  <strong>Título:</strong> ${obra.titulo}<br>
                  <strong>Precio USD:</strong> U$S ${obra.precio_usd.toLocaleString()}<br>
                  <strong>Precio Pesos:</strong> $ ${obra.precio_pesos.toLocaleString()}<br>
                  <strong>Medidas:</strong> ${obra.medidas}
               </li>
               `;
            });

            datomail += "</ul>";

            const mailadmin = administradores.length
               ? administradores.map((admin) => admin.email).join(",")
               : "";

            if (mailadmin) {
               await Enviarmail({
                  aquien: mailadmin,
                  reply_to: mailadmin,
                  asunto: "Compra Realizada - " + userRecord.nombre,
                  texto: datomail,
               });
            }

            clearCart();
            alert("Pedido registrado correctamente. Ahora puede enviar su comprobante de transferencia.");
            navigate("/mispedidos");
         }
      } catch (error) {
         console.error("Purchase error:", error);
         alert(`Error al procesar la compra: ${error.message}`);
      }

      setIsProcessing(false);
   };

   if (cartObras.length === 0) {
      return (
         <main className="min-h-screen bg-[#FAFAF8] px-6 py-16">
            <div className="mx-auto max-w-4xl border border-[#EDE9E4] bg-white p-10 text-center md:p-16">
               <p className="mb-4 text-[0.68rem] uppercase tracking-[0.24em] text-[#9B6D3F]">
                  Carrito
               </p>
               <h1
                  className="text-4xl font-medium text-[#1A1A1A] md:text-5xl"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
               >
                  No hay obras reservadas
               </h1>
               <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-[#7A7570]">
                  Cuando agregue piezas a su carrito, aparecerán aquí con el resumen del pedido y los datos de entrega.
               </p>
               <div className="mt-10 flex justify-center">
                  <Link
                     to="/obras"
                     className="inline-flex items-center gap-2 bg-[#9B6D3F] px-6 py-3 text-[0.72rem] uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#6B4A2A]"
                  >
                     <ArrowLeft className="h-4 w-4" />
                     Volver a obras
                  </Link>
               </div>
            </div>
         </main>
      );
   }

   return (
      <main className="min-h-screen bg-[#FAFAF8] px-6 py-12 md:py-16">
         <div className="mx-auto max-w-7xl">
            <div className="mb-12">
               <p className="mb-4 text-[0.68rem] uppercase tracking-[0.24em] text-[#9B6D3F]">
                  Checkout
               </p>
               <h1
                  className="text-4xl font-medium leading-[1.02] text-[#1A1A1A] md:text-6xl"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
               >
                  Finalizar compra
               </h1>
               <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#7A7570]">
                  Revise sus obras, confirme sus datos y elija el medio de pago para convertir su selección en un pedido formal.
               </p>
            </div>

            <div className="grid gap-8 xl:grid-cols-[1.5fr_0.9fr]">
               <section className="space-y-8">
                  <div className="border border-[#EDE9E4] bg-white p-6 md:p-8">
                     <div className="mb-6 flex items-center gap-3">
                        <ShoppingBag className="h-5 w-5 text-[#9B6D3F]" />
                        <h2
                           className="text-xl font-medium text-[#1A1A1A]"
                           style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                        >
                           Obras seleccionadas
                        </h2>
                     </div>

                     <div className="space-y-4">
                        {cartObras.map((obra) => (
                           <article
                              key={obra.id}
                              className="grid gap-4 border-t border-[#EDE9E4] pt-4 first:border-t-0 first:pt-0 sm:grid-cols-[110px_1fr_auto]"
                           >
                              <div className="aspect-[4/5] overflow-hidden bg-[#F5F0EB]">
                                 {obra.imagenes?.[0]?.imagen_url ? (
                                    <img
                                       src={obra.imagenes[0].imagen_url}
                                       alt={obra.titulo}
                                       className="h-full w-full object-cover"
                                    />
                                 ) : (
                                    <div className="flex h-full items-center justify-center text-xs uppercase tracking-[0.14em] text-[#9B6D3F]">
                                       Sin imagen
                                    </div>
                                 )}
                              </div>

                              <div className="space-y-2">
                                 <h3
                                    className="text-xl font-medium text-[#1A1A1A]"
                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                 >
                                    {obra.titulo}
                                 </h3>
                                 <p className="text-sm text-[#7A7570]">
                                    Medidas: {obra.medidas || "No especificadas"}
                                 </p>
                              </div>

                              <div className="flex flex-col items-start gap-3 sm:items-end">
                                 <p className="text-sm uppercase tracking-[0.12em] text-[#9B6D3F]">
                                    U$S {Number(obra.precio).toLocaleString("es-AR")}
                                 </p>
                                 <button
                                    type="button"
                                    onClick={() => handleDelete(obra.id)}
                                    disabled={isProcessing}
                                    className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.12em] text-[#7A7570] transition-colors hover:text-red-600 disabled:opacity-40"
                                 >
                                    <Trash2 className="h-4 w-4" />
                                    Quitar
                                 </button>
                              </div>
                           </article>
                        ))}
                     </div>
                  </div>

                  <div className="grid gap-8 lg:grid-cols-2">
                     <div className="border border-[#EDE9E4] bg-white p-6 md:p-8">
                        <div className="mb-6 flex items-center gap-3">
                           <User className="h-5 w-5 text-[#9B6D3F]" />
                           <h2
                              className="text-xl font-medium text-[#1A1A1A]"
                              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                           >
                              Datos personales
                           </h2>
                        </div>

                        {userFound && (
                           <div className="mb-5 border-l-2 border-emerald-500 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                              Datos de usuario cargados automáticamente.
                           </div>
                        )}

                        <div className="space-y-5">
                           <label className="block">
                              <span className="mb-2 block text-[0.68rem] uppercase tracking-[0.15em] text-[#7A7570]">
                                 Email
                              </span>
                              <div className="relative">
                                 <Mail className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9B6D3F]" />
                                 <input
                                    type="email"
                                    name="email"
                                    value={userRecord.email}
                                    onChange={handleChange}
                                    disabled={isProcessing}
                                    className="w-full border-b border-[#EDE9E4] bg-transparent pb-2 pl-6 text-sm text-[#1A1A1A] outline-none transition-colors focus:border-[#9B6D3F]"
                                    placeholder="Email"
                                 />
                              </div>
                           </label>

                           <label className="block">
                              <span className="mb-2 block text-[0.68rem] uppercase tracking-[0.15em] text-[#7A7570]">
                                 Nombre completo
                              </span>
                              <div className="relative">
                                 <User className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9B6D3F]" />
                                 <input
                                    type="text"
                                    name="nombre"
                                    value={userRecord.nombre}
                                    onChange={handleChange}
                                    disabled={isProcessing}
                                    className="w-full border-b border-[#EDE9E4] bg-transparent pb-2 pl-6 text-sm text-[#1A1A1A] outline-none transition-colors focus:border-[#9B6D3F]"
                                    placeholder="Nombre completo"
                                 />
                              </div>
                           </label>

                           <label className="block">
                              <span className="mb-2 block text-[0.68rem] uppercase tracking-[0.15em] text-[#7A7570]">
                                 Celular
                              </span>
                              <div className="relative">
                                 <Phone className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9B6D3F]" />
                                 <input
                                    type="text"
                                    name="celular"
                                    value={userRecord.celular}
                                    onChange={handleChange}
                                    disabled={isProcessing}
                                    className="w-full border-b border-[#EDE9E4] bg-transparent pb-2 pl-6 text-sm text-[#1A1A1A] outline-none transition-colors focus:border-[#9B6D3F]"
                                    placeholder="Celular"
                                 />
                              </div>
                           </label>
                        </div>
                     </div>

                     <div className="border border-[#EDE9E4] bg-white p-6 md:p-8">
                        <div className="mb-6 flex items-center gap-3">
                           <MapPin className="h-5 w-5 text-[#9B6D3F]" />
                           <h2
                              className="text-xl font-medium text-[#1A1A1A]"
                              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                           >
                              Entrega
                           </h2>
                        </div>

                        <div className="space-y-5">
                           {[
                              ["calle", "Calle"],
                              ["ciudad", "Ciudad"],
                              ["provincia", "Provincia"],
                              ["codigo_postal", "Código postal"],
                           ].map(([name, label]) => (
                              <label key={name} className="block">
                                 <span className="mb-2 block text-[0.68rem] uppercase tracking-[0.15em] text-[#7A7570]">
                                    {label}
                                 </span>
                                 <input
                                    type="text"
                                    name={name}
                                    value={dirRecord[name]}
                                    onChange={handleDirChange}
                                    disabled={isProcessing}
                                    className="w-full border-b border-[#EDE9E4] bg-transparent pb-2 text-sm text-[#1A1A1A] outline-none transition-colors focus:border-[#9B6D3F]"
                                    placeholder={label}
                                 />
                              </label>
                           ))}
                        </div>
                     </div>
                  </div>
               </section>

               <aside className="space-y-6 xl:sticky xl:top-24 xl:self-start">
                  <div className="border border-[#EDE9E4] bg-white p-6 md:p-8">
                     <div className="mb-6 flex items-center gap-3">
                        <ShieldCheck className="h-5 w-5 text-[#9B6D3F]" />
                        <h2
                           className="text-xl font-medium text-[#1A1A1A]"
                           style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                        >
                           Resumen
                        </h2>
                     </div>

                     <div className="space-y-4 border-b border-[#EDE9E4] pb-6">
                        <div className="flex items-center justify-between text-sm text-[#7A7570]">
                           <span>Obras</span>
                           <span>{cartObras.length}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm text-[#7A7570]">
                           <span>Total USD</span>
                           <span>U$S {totalUSD.toLocaleString("es-AR")}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm text-[#7A7570]">
                           <span>Dólar de referencia</span>
                           <span>$ {dolarRate.toLocaleString("es-AR")}</span>
                        </div>
                     </div>

                     <div className="pt-6">
                        <p className="text-[0.68rem] uppercase tracking-[0.18em] text-[#7A7570]">
                           Estimado en pesos
                        </p>
                        <p
                           className="mt-2 text-3xl font-medium text-[#1A1A1A]"
                           style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                        >
                           $ {totalPagoPesosFormatted}
                        </p>
                     </div>
                  </div>

                  <div className="border border-[#EDE9E4] bg-white p-6 md:p-8">
                     <div className="mb-6">
                        <h2
                           className="text-xl font-medium text-[#1A1A1A]"
                           style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                        >
                           Método de pago
                        </h2>
                     </div>

                     <div className="space-y-3">
                        <label className={`block cursor-pointer border p-4 transition-colors ${paymentMethod === "transferencia" ? "border-[#9B6D3F] bg-[#F5F0EB]" : "border-[#EDE9E4] hover:border-[#9B6D3F]"}`}>
                           <div className="flex items-start gap-3">
                              <input
                                 type="radio"
                                 name="paymentMethod"
                                 value="transferencia"
                                 checked={paymentMethod === "transferencia"}
                                 onChange={() => setPaymentMethod("transferencia")}
                                 className="mt-1"
                              />
                              <div>
                                 <div className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
                                    <Landmark className="h-4 w-4 text-[#9B6D3F]" />
                                    Transferencia bancaria
                                 </div>
                                 <p className="mt-1 text-sm leading-relaxed text-[#7A7570]">
                                    Confirme el pedido y envíe luego el comprobante con los datos indicados.
                                 </p>
                              </div>
                           </div>
                        </label>


                     </div>

                     {paymentMethod === "transferencia" && (
                        <TransferInstructions
                           ref={divTransfRef}
                           tabla={tabla}
                           textoPago={textoPago}
                        />
                     )}
                  </div>

                  <div className="space-y-3">
                     <button
                        type="button"
                        onClick={handleComprar}
                        disabled={isProcessing || !isFormComplete()}
                        className="w-full bg-[#9B6D3F] px-6 py-4 text-[0.72rem] uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#6B4A2A] disabled:cursor-not-allowed disabled:opacity-40"
                     >
                        {isProcessing ? "Procesando..." : textoPago || "Confirmar compra"}
                     </button>
                     <button
                        type="button"
                        onClick={handleCancelacion}
                        disabled={isProcessing}
                        className="w-full border border-[#EDE9E4] px-6 py-4 text-[0.72rem] uppercase tracking-[0.18em] text-[#7A7570] transition-colors hover:border-red-500 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                     >
                        Cancelar compra
                     </button>
                  </div>
               </aside>
            </div>
         </div>
      </main>
   );
}